import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Download, CalendarCheck } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const CSV_LABEL = { present: "Present", off: "Off", leave: "Leave", none: "Report Not Submitted" };
const CELL = {
  present: { t: "P", cls: "bg-emerald-500/15 text-emerald-700" },
  off: { t: "O", cls: "bg-amber-500/15 text-amber-700" },
  leave: { t: "L", cls: "bg-sky-500/15 text-sky-700" },
  none: { t: "—", cls: "bg-destructive/10 text-destructive" },
};

export default function AttendanceView() {
  const [start, setStart] = useState(daysAgo(13));
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/reports/attendance", { params: { start, end } }); setData(data); }
    catch (e) { toast.error("Could not load attendance"); }
    finally { setLoading(false); }
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const q = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
  const downloadCsv = () => {
    if (!data) return;
    const lines = [["Member", "Role", ...data.dates].map(q).join(",")];
    data.rows.forEach((r) => {
      lines.push([q(r.name), q(r.role === "manager" ? "Manager" : "User"),
        ...data.dates.map((d) => q(CSV_LABEL[r.by_date[d]] || "Report Not Submitted"))].join(","));
    });
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `attendance_${start}_to_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Attendance CSV downloaded");
  };

  return (
    <div data-testid="attendance-view">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><CalendarCheck className="h-5 w-5" /></div>
        <div>
          <h2 className="font-heading text-xl font-bold">Attendance</h2>
          <p className="text-sm text-muted-foreground">Present = report submitted · Off / Leave as marked · otherwise Report Not Submitted.</p>
        </div>
      </div>

      <Card className="p-5 sm:p-6 rounded-xl mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5"><Label>From</Label><Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} className="w-44" data-testid="attendance-start-input" /></div>
          <div className="space-y-1.5"><Label>To</Label><Input type="date" value={end} max={today()} onChange={(e) => setEnd(e.target.value)} className="w-44" data-testid="attendance-end-input" /></div>
          <div className="flex gap-2 flex-wrap">
            {[[6, "7 days"], [13, "14 days"], [29, "30 days"]].map(([n, l]) => (
              <Button key={n} variant="secondary" size="sm" className="rounded-full" onClick={() => { setStart(daysAgo(n)); setEnd(today()); }} data-testid={`attendance-preset-${n}`}>{l}</Button>
            ))}
          </div>
          <Button className="ml-auto rounded-lg" onClick={downloadCsv} disabled={!data || loading} data-testid="attendance-download-csv"><Download className="h-4 w-4 mr-1.5" /> Download CSV</Button>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : !data || data.rows.length === 0 ? (
        <Card className="p-12 text-center rounded-xl" data-testid="attendance-empty"><p className="text-muted-foreground">No members to show.</p></Card>
      ) : (
        <Card className="p-3 sm:p-4 rounded-xl overflow-x-auto" data-testid="attendance-table">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 bg-card text-left font-semibold px-2 py-2 min-w-[140px] z-10">Member</th>
                {data.dates.map((d) => <th key={d} className="px-1.5 py-2 text-[10px] font-medium text-muted-foreground whitespace-nowrap">{d.slice(5)}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.user_id} className="border-t border-border" data-testid={`attendance-row-${r.user_id}`}>
                  <td className="sticky left-0 bg-card px-2 py-1.5 whitespace-nowrap z-10">
                    <span className="font-medium">{r.name}</span>
                    <span className="ml-1.5 text-[10px] text-muted-foreground">{r.role === "manager" ? "Mgr" : "User"}</span>
                  </td>
                  {data.dates.map((d) => {
                    const c = CELL[r.by_date[d]] || CELL.none;
                    return <td key={d} className="px-1 py-1 text-center"><span className={`inline-flex h-6 w-6 items-center justify-center rounded text-[11px] font-semibold ${c.cls}`} title={CSV_LABEL[r.by_date[d]]}>{c.t}</span></td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex flex-wrap gap-4 mt-4 text-[11px] text-muted-foreground px-1">
            <span className="flex items-center gap-1"><span className="h-4 w-4 rounded bg-emerald-500/15 text-emerald-700 inline-flex items-center justify-center text-[10px] font-semibold">P</span> Present</span>
            <span className="flex items-center gap-1"><span className="h-4 w-4 rounded bg-amber-500/15 text-amber-700 inline-flex items-center justify-center text-[10px] font-semibold">O</span> Off</span>
            <span className="flex items-center gap-1"><span className="h-4 w-4 rounded bg-sky-500/15 text-sky-700 inline-flex items-center justify-center text-[10px] font-semibold">L</span> Leave</span>
            <span className="flex items-center gap-1"><span className="h-4 w-4 rounded bg-destructive/10 text-destructive inline-flex items-center justify-center text-[10px] font-semibold">—</span> Report Not Submitted</span>
          </div>
        </Card>
      )}
    </div>
  );
}
